import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../services/supabase';
import { useAuth } from './AuthContext.jsx';
import { useBusiness } from './BusinessContext';

const LotsContext = createContext();

export const LotsProvider = ({ children }) => {
  const { user, sessionChecked } = useAuth();
  const { business } = useBusiness();
  const [lots, setLots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lightningLotsCount, setLightningLotsCount] = useState(0);
  const [pendingReviewsCount, setPendingReviewsCount] = useState(0);
  
  // Contador para IDs amigables
  const [nextDisplayId, setNextDisplayId] = useState(1);

  const [catalogStatus, setCatalogStatus] = useState({
    isOnline: true, // Por defecto online
    showPrices: true,
    showQuantities: true
  });

  useEffect(() => {
    // Solo reaccionar a cambios de user o business cuando la sesión ya fue verificada
    if (!sessionChecked || !business?.id) {
      console.log('LotsContext - Esperando verificación de sesión o business...');
      return;
    }

    console.log('LotsContext - User/Business state changed:', { userId: user?.id, businessId: business?.id });
    if (user) {
      console.log('LotsContext - Calling fetchLots because user and business are present');
      fetchLots();
      fetchCatalogSettings(); // Cargar configuración del catálogo
    } else {
      console.log('LotsContext - Clearing lots because no user');
      setLots([]); // Limpiar lotes si no hay usuario
    }
  }, [user, business?.id, sessionChecked]);

  const formatDisplayId = (num) => {
    return `L#${String(num).padStart(3, '0')}`;
  };

  const fetchLots = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('=== DEBUG LOTES RELÁMPAGO EN INVENTARIO ===');
      
      const { data, error } = await supabase
        .from('lots')
        .select('*, lot_movements(*)')
        .eq('auth_user_id', user.id)
        .eq('business_id', business.id)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Contar todos los lotes con status 'lightning'
      const lightningLots = data.filter(lot => lot.status === 'lightning');
      console.log('=== DEBUG LOTES RELÁMPAGO ===');
      console.log('Total lotes relámpago encontrados:', lightningLots.length);
      console.log('Detalles completos:', lightningLots);
      
      setLightningLotsCount(lightningLots.length);
      console.log('Estado actualizado - lightningLotsCount:', lightningLots.length);

      // Transformar los datos para el formato que espera la UI
      const formattedLots = data.map(lot => ({
        id: lot.id,
        display_id: lot.display_id || formatDisplayId(lot.id),
        title: lot.title || lot.name,
        supplier: lot.supplier_name,
        quantity: lot.remaining_quantity,
        initial_quantity: lot.initial_quantity,
        unit: lot.unit_sign,
        unitPrice: lot.unit_price,
        unitCost: lot.unit_cost,
        container_deposit: lot.container_deposit,
        notes: lot.observation,
        image: lot.image_url,
        timestamp: lot.created_at,
        status: lot.status,
        needs_reconciliation: lot.needs_reconciliation,
        reconciliation_status: lot.reconciliation_status,
        counted_by: lot.counted_by,
        counted_at: lot.counted_at,
        is_public: lot.is_public || true,
        movements: [
          // Movement inicial
          {
            type: 'add',
            quantity: lot.initial_quantity,
            unit: lot.unit_sign,
            timestamp: lot.created_at,
            note: 'Ingreso inicial'
          },
          // Movements de la base de datos
          ...(lot.lot_movements || []).map(m => ({
            type: m.type,
            quantity: m.quantity,
            unit: m.unit_sign,
            timestamp: m.created_at,
            note: m.note,
            auth_user_id: m.auth_user_id,
            previous_remaining_quantity: m.previous_remaining_quantity,
            new_remaining_quantity: m.new_remaining_quantity
          }))
        ].sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp)) // Ordenar por timestamp, más reciente primero
      }));

      console.log('LotsContext - Transformed lots:', formattedLots);

      // Actualizar el nextDisplayId basado en los datos reales
      const maxDisplayId = formattedLots.reduce((max, lot) => {
        const match = lot.display_id?.match(/L#(\d+)/);
        const num = match ? parseInt(match[1]) : 0;
        return Math.max(max, num);
      }, 0);

      setNextDisplayId(maxDisplayId + 1);
      setLots(formattedLots);
      console.log('LotsContext - State updated with lots:', formattedLots.length);

      // En fetchLots, después de contar los lotes relámpago:
      const pendingReviews = data.filter(lot => 
        lot.reconciliation_status === 'pending' || lot.reconciliation_status === 'in_progress'
      );
      setPendingReviewsCount(pendingReviews.length);
    } catch (error) {
      console.error('Error fetching lots:', error);
      setError(error.message);
      setLots([]);
      setLightningLotsCount(0);
    } finally {
      setLoading(false);
    }
  };

  const addLot = async (lotData) => {
    try {
      const displayId = formatDisplayId(nextDisplayId);
      console.log('1. Generando displayId:', displayId);
      
      if (!user || !business?.id) {
        console.log('Estado actual:', { user, businessId: business?.id });
        throw new Error('Debes estar logueado y tener un negocio seleccionado para crear un lote');
      }
      console.log('Usuario y negocio identificados:', { userId: user.id, businessId: business.id });

      // Preparar datos para Supabase
      const now = new Date().toISOString();
      const supabaseLotData = {
        name: lotData.name || lotData.title,
        supplier_name: lotData.supplier || null,
        initial_quantity: Number(lotData.quantity) || 0,
        remaining_quantity: Number(lotData.remaining_quantity !== undefined ? lotData.remaining_quantity : lotData.quantity) || 0,
        counted_quantity: null,
        unit_sign: lotData.unit || 'bultos',
        unit_price: lotData.unitPrice ? parseFloat(lotData.unitPrice) : null,
        unit_cost: lotData.unitCost ? parseFloat(lotData.unitCost) : null,
        container_deposit: lotData.container_deposit ? parseFloat(lotData.container_deposit) : null,
        is_public: true,
        observation: lotData.notes || null,
        image_url: lotData.image || null,
        status: lotData.status || 'active',
        needs_reconciliation: lotData.needs_reconciliation || false,
        display_id: displayId,
        auth_user_id: user.id,
        business_id: business.id,
        created_at: now,
        updated_at: now,
        deleted_at: null,
        deactivation_reason: null,
        closed_at: null,
        closed_by: null,
        closure_reason: null,
        reconciliation_status: null,
        counted_by: null,
        remainingqty_different: null,
        counted_at: null,
      };
      console.log('2. Datos preparados para Supabase:', supabaseLotData);

      // Insertar en Supabase
      let { data: newSupabaseLot, error: insertError } = await supabase
        .from('lots')
        .insert([supabaseLotData])
        .select()
        .single();

      console.log('3. Respuesta de inserción:', { newSupabaseLot, insertError });

      if (insertError) {
        console.log('4. Error en inserción, intentando sin display_id');
        // Si falla con display_id, intentar sin él
        const { display_id, ...dataWithoutDisplayId } = supabaseLotData;
        const result = await supabase
          .from('lots')
          .insert([dataWithoutDisplayId])
          .select()
          .single();
        
        newSupabaseLot = result.data;
        insertError = result.error;
        
        console.log('5. Resultado sin display_id:', { newSupabaseLot, insertError });
      }

      if (insertError) throw insertError;

      // Si la inserción fue exitosa, actualizar el estado local
      if (newSupabaseLot) {
        // Mantener el formato actual para compatibilidad
        const formattedLot = {
          id: newSupabaseLot.id,
          display_id: displayId,
          title: newSupabaseLot.name,
          supplier: newSupabaseLot.supplier_name,
          quantity: newSupabaseLot.remaining_quantity,
          initial_quantity: newSupabaseLot.initial_quantity,
          unit: newSupabaseLot.unit_sign,
          unitPrice: newSupabaseLot.unit_price,
          unitCost: newSupabaseLot.unit_cost,
          container_deposit: newSupabaseLot.container_deposit,
          notes: newSupabaseLot.observation,
          image: newSupabaseLot.image_url,
          timestamp: newSupabaseLot.created_at,
          status: newSupabaseLot.status,
          needs_reconciliation: newSupabaseLot.needs_reconciliation,
          reconciliation_status: newSupabaseLot.reconciliation_status,
          counted_by: newSupabaseLot.counted_by,
          counted_at: newSupabaseLot.counted_at,
          is_public: newSupabaseLot.is_public || true,
          movements: [
            {
              type: 'add',
              quantity: newSupabaseLot.initial_quantity,
              unit: newSupabaseLot.unit_sign,
              timestamp: newSupabaseLot.created_at,
              note: 'Ingreso inicial'
            }
          ]
        };

        setLots(currentLots => [formattedLot, ...currentLots]);
        setNextDisplayId(current => current + 1);
        return formattedLot;
      }
    } catch (error) {
      console.error('Error adding lot:', error);
      throw error;
    }
  };

  const updateLot = async (id, updates) => {
    try {
      // Obtener el lote actual para comparar cambios
      const currentLot = lots.find(lot => lot.id === id);
      const quantityChanged = currentLot && updates.quantity !== undefined && 
                             Number(updates.quantity) !== Number(currentLot.quantity);
      
      // 1. Actualizar en el estado local para inmediatez
      setLots(currentLots => 
        currentLots.map(lot => 
          lot.id === id ? { 
            ...lot, 
            ...updates,
            quantity: updates.quantity !== undefined ? Number(updates.quantity) : lot.quantity
          } : lot
        )
      );

      const now = new Date().toISOString();
      
      // 2. Preparar los datos para Supabase
      const supabaseUpdates = {
        name: updates.title,
        supplier_name: updates.supplier,
        unit_price: updates.unitPrice,
        unit_cost: updates.unitCost,
        container_deposit: updates.container_deposit,
        observation: updates.notes,
        updated_at: now
      };
      
      // Si la cantidad cambió, actualizarla en Supabase
      if (quantityChanged) {
        supabaseUpdates.remaining_quantity = Number(updates.quantity);
      }
      
      // Filtrar valores undefined/null para no sobrescribir con null datos existentes
      Object.keys(supabaseUpdates).forEach(key => 
        supabaseUpdates[key] === undefined && delete supabaseUpdates[key]
      );
      
      console.log('Actualizando lote en Supabase:', supabaseUpdates);
      
      // 3. Actualizar en Supabase
      const { error: updateError } = await supabase
        .from('lots')
        .update(supabaseUpdates)
        .eq('id', id);
        
      if (updateError) throw updateError;
      
      // 4. Registrar movimiento según los cambios
      if (quantityChanged) {
        // Calcular la diferencia de cantidad
        const quantityDiff = Number(updates.quantity) - Number(currentLot.quantity);
        const movementType = quantityDiff > 0 ? 'add' : 'remove';
        const absQuantityDiff = Math.abs(quantityDiff);
        
        // Registrar movimiento de ajuste de cantidad
        const { error: movementError } = await supabase
          .from('lot_movements')
          .insert({
            lot_id: id,
            type: movementType,
            quantity: absQuantityDiff,
            note: `Ajuste manual de cantidad: ${quantityDiff > 0 ? '+' : '-'}${absQuantityDiff}`,
            created_at: now,
            auth_user_id: user.id,
            business_id: business.id,
            unit_sign: currentLot.unit,
            previous_remaining_quantity: Number(currentLot.quantity),
            new_remaining_quantity: Number(updates.quantity)
          });
          
        if (movementError) console.error('Error registrando movimiento de ajuste:', movementError);
      } else {
        // Registrar movimiento de edición general si no hubo cambio de cantidad
        const { error: movementError } = await supabase
          .from('lot_movements')
          .insert({
            lot_id: id,
            type: 'edit',
            quantity: 0,
            note: 'Información del lote actualizada',
            created_at: now,
            auth_user_id: user.id,
            business_id: business.id,
            unit_sign: currentLot.unit,
            previous_remaining_quantity: Number(currentLot.quantity),
            new_remaining_quantity: Number(currentLot.quantity)
          });
          
        if (movementError) console.error('Error registrando movimiento de edición:', movementError);
      }
      
      // Recargar los lotes para asegurar que tenemos los datos más actualizados
      await fetchLots();
      
      return true;
    } catch (error) {
      console.error('Error actualizando lote:', error);
      throw error;
    }
  };

  const updateInventory = async (lotId, quantity, type = 'remove', note = '') => {
    try {
      const changeAmount = Number(quantity) || 0;
      const now = new Date().toISOString();
      
      // 1. Obtener el lote actual
      const { data: lot, error: lotError } = await supabase
        .from('lots')
        .select('remaining_quantity, unit_sign')
        .eq('id', lotId)
        .eq('business_id', business.id)
        .single();

      if (lotError) throw lotError;

      const currentQuantity = Number(lot.remaining_quantity) || 0;
      const newQuantity = type === 'remove' ? 
        currentQuantity - changeAmount : 
        currentQuantity + changeAmount;

      // 2. Actualizar remaining_quantity en lots
      const { error: updateError } = await supabase
        .from('lots')
        .update({ 
          remaining_quantity: newQuantity,
          updated_at: now
        })
        .eq('id', lotId);

      if (updateError) throw updateError;

      // 3. Registrar el movimiento
      const { error: movementError } = await supabase
        .from('lot_movements')
        .insert({
          lot_id: lotId,
          auth_user_id: user.id,
          business_id: business.id,
          type,
          quantity: changeAmount,
          unit_sign: lot.unit_sign,
          previous_remaining_quantity: currentQuantity,
          new_remaining_quantity: newQuantity,
          note,
          created_at: now,
          updated_at: now
        });

      if (movementError) throw movementError;

      // 4. Si todo fue exitoso, actualizar el estado local
      setLots(currentLots => 
        currentLots.map(lot => {
          if (lot.id === lotId) {
            return {
              ...lot,
              quantity: newQuantity,
              movements: [
                {
                  type,
                  quantity: changeAmount,
                  unit: lot.unit_sign,
                  timestamp: now,
                  note
                },
                ...lot.movements
              ]
            };
          }
          return lot;
        })
      );
    } catch (error) {
      console.error('Error updating inventory:', error);
      throw error;
    }
  };

  const startLotReview = async (lotId, initialQuantity, note) => {
    try {
      const now = new Date().toISOString();
      
      // 1. Obtener el lote actual para tener la unidad de medida
      const { data: lot, error: lotError } = await supabase
        .from('lots')
        .select('unit_sign, remaining_quantity')
        .eq('id', lotId)
        .single();

      if (lotError) throw lotError;

      // 2. Actualizar el estado del lote
      const { error: lotUpdateError } = await supabase
        .from('lots')
        .update({ 
          reconciliation_status: 'in_progress',
          needs_reconciliation: true,
          updated_at: now
        })
        .eq('id', lotId);

      if (lotUpdateError) throw lotUpdateError;

      // 3. Crear el movimiento inicial del repaso con todos los campos requeridos
      const { error: movementError } = await supabase
        .from('lot_movements')
        .insert({
          lot_id: lotId,
          auth_user_id: user.id,
          type: 'quality_check_start',
          quantity: initialQuantity,
          unit_sign: lot.unit_sign,
          previous_remaining_quantity: lot.remaining_quantity,
          new_remaining_quantity: lot.remaining_quantity,
          note: note || 'Inicio de control de calidad',
          created_at: now,
          updated_at: now
        });

      if (movementError) throw movementError;

      // 4. Actualizar estado local
      setLots(currentLots => 
        currentLots.map(lot => 
          lot.id === lotId 
            ? {
                ...lot,
                reconciliation_status: 'in_progress',
                needs_reconciliation: true,
                movements: [
                  {
                    type: 'quality_check_start',
                    quantity: initialQuantity,
                    unit: lot.unit_sign,
                    timestamp: now,
                    note: note || 'Inicio de control de calidad'
                  },
                  ...lot.movements
                ]
              }
            : lot
        )
      );

      return true;
    } catch (error) {
      console.error('Error starting lot review:', error);
      throw error;
    }
  };

  const finishLotReview = async (lotId, finalQuantity, note) => {
    try {
      const now = new Date().toISOString();
      
      // 1. Obtener el lote y su último movimiento de repaso
      const { data: lot, error: lotError } = await supabase
        .from('lots')
        .select('*, lot_movements(*)')
        .eq('id', lotId)
        .single();

      if (lotError) throw lotError;

      const initialQuantity = lot.remaining_quantity;
      const difference = finalQuantity - initialQuantity;
      const reviewType = difference < 0 ? 'baja' : difference > 0 ? 'rinde' : 'igual';

      // 2. Actualizar el estado del lote
      const { error: updateError } = await supabase
        .from('lots')
        .update({ 
          reconciliation_status: 'pending',
          needs_reconciliation: true,
          updated_at: now
        })
        .eq('id', lotId);

      if (updateError) throw updateError;

      // 3. Registrar el movimiento final del repaso
      const { error: movementError } = await supabase
        .from('lot_movements')
        .insert({
          lot_id: lotId,
          auth_user_id: user.id,
          type: 'quality_check_end',
          quantity: finalQuantity,
          unit_sign: lot.unit_sign,
          previous_remaining_quantity: initialQuantity,
          new_remaining_quantity: finalQuantity,
          note: `${reviewType}: ${note || ''} (${initialQuantity} → ${finalQuantity})`,
          created_at: now,
          updated_at: now
        });

      if (movementError) throw movementError;

      // 4. Actualizar estado local
      setLots(currentLots => 
        currentLots.map(lot => 
          lot.id === lotId 
            ? {
                ...lot,
                reconciliation_status: 'pending',
                needs_reconciliation: true,
                movements: [
                  {
                    type: 'quality_check_end',
                    quantity: difference,
                    unit: lot.unit_sign,
                    timestamp: now,
                    note: `${reviewType}: ${note || ''} (${initialQuantity} → ${finalQuantity})`
                  },
                  ...lot.movements
                ]
              }
            : lot
        )
      );

      // 5. Actualizar contador de repasos pendientes
      setPendingReviewsCount(current => current + 1);

      return true;
    } catch (error) {
      console.error('Error finishing lot review:', error);
      throw error;
    }
  };

  const approveLotReview = async (lotId, approvalNote) => {
    try {
      const now = new Date().toISOString();
      
      // 1. Obtener el lote y su último movimiento de repaso
      const { data: lot, error: lotError } = await supabase
        .from('lots')
        .select('*, lot_movements(*)')
        .eq('id', lotId)
        .single();

      if (lotError) throw lotError;

      const lastReview = lot.lot_movements
        .filter(m => m.type === 'quality_check_end')
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0];

      if (!lastReview) throw new Error('No se encontró el control de calidad pendiente');

      // 2. Actualizar el lote con la cantidad final y marcar como confirmado
      const { error: updateError } = await supabase
        .from('lots')
        .update({ 
          remaining_quantity: lastReview.new_remaining_quantity,
          reconciliation_status: 'confirmed',
          needs_reconciliation: false,
          observation: approvalNote || '',
          counted_by: user.id,
          counted_at: now,
          updated_at: now
        })
        .eq('id', lotId);

      if (updateError) throw updateError;

      // 3. Registrar el movimiento de aprobación
      const { error: movementError } = await supabase
        .from('lot_movements')
        .insert({
          lot_id: lotId,
          auth_user_id: user.id,
          type: 'quality_check_approved',
          quantity: lastReview.new_remaining_quantity,
          unit_sign: lot.unit_sign,
          previous_remaining_quantity: lastReview.previous_remaining_quantity,
          new_remaining_quantity: lastReview.new_remaining_quantity,
          note: approvalNote || 'Control de calidad aprobado',
          created_at: now,
          updated_at: now
        });

      if (movementError) throw movementError;

      // 4. Actualizar estado local
      setLots(currentLots => 
        currentLots.map(lot => 
          lot.id === lotId 
            ? {
                ...lot,
                quantity: lastReview.new_remaining_quantity,
                reconciliation_status: 'confirmed',
                needs_reconciliation: false,
                movements: [
                  {
                    type: 'quality_check_approved',
                    quantity: lastReview.new_remaining_quantity - lastReview.previous_remaining_quantity,
                    unit: lot.unit_sign,
                    timestamp: now,
                    note: approvalNote || 'Control de calidad aprobado'
                  },
                  ...lot.movements
                ]
              }
            : lot
        )
      );

      // 5. Actualizar contador de repasos pendientes
      setPendingReviewsCount(current => current - 1);

      return true;
    } catch (error) {
      console.error('Error approving lot review:', error);
      throw error;
    }
  };

  const updateCatalogStatus = async (newStatus) => {
    try {
      // Actualizar el estado local primero para una respuesta inmediata en la UI
      const updatedStatus = { ...catalogStatus, ...newStatus };
      setCatalogStatus(updatedStatus);
      
      // Persistir los cambios en la base de datos
      if (business?.id) {
        console.log('Guardando configuración de catálogo en Supabase:', updatedStatus);
        
        // Intentar actualizar la columna catalog_settings si existe
        try {
          const { error } = await supabase
            .from('businesses')
            .update({ catalog_settings: updatedStatus })
            .eq('id', business.id);
            
          if (error) {
            console.error('Error al guardar configuración en catalog_settings:', error);
            
            // Si falla, intentar guardar en la tabla catalog_settings
            const { error: settingsError } = await supabase
              .from('catalog_settings')
              .upsert({ 
                business_id: business.id,
                settings: updatedStatus,
                updated_at: new Date().toISOString()
              }, { onConflict: 'business_id' });
              
            if (settingsError) {
              console.error('Error al guardar en tabla catalog_settings:', settingsError);
              throw settingsError;
            }
          }
        } catch (dbError) {
          console.error('Error al guardar configuración del catálogo:', dbError);
          // No lanzamos el error para no interrumpir la experiencia del usuario
        }
      }
    } catch (error) {
      console.error('Error updating catalog status:', error);
      // No lanzamos el error para mantener el estado local actualizado
    }
  };

  // Nueva función para reconciliar lotes relámpago
  const reconcileLightningLot = async (lotId, initialQuantity) => {
    try {
      const now = new Date().toISOString();
      
      // 1. Obtener información completa del lote
      const { data: lot, error: lotError } = await supabase
        .from('lots')
        .select('*, lot_movements(*)')
        .eq('id', lotId)
        .single();
      
      if (lotError) throw lotError;
      
      // 2. El remaining_quantity ya contiene el total vendido (como valor negativo)
      // Calculamos el remaining_quantity final: initial_quantity + remaining_quantity (que es negativo)
      const remainingQuantity = Number(initialQuantity) + Number(lot.remaining_quantity || 0);
      
      // 3. Actualizar el lote
      const { error: updateError } = await supabase
        .from('lots')
        .update({ 
          initial_quantity: initialQuantity,
          remaining_quantity: remainingQuantity,
          status: 'active',
          needs_reconciliation: false,
          updated_at: now
        })
        .eq('id', lotId);
      
      if (updateError) throw updateError;
      
      // 4. Registrar movimiento de reconciliación con el nuevo tipo específico
      const { error: movementError } = await supabase
        .from('lot_movements')
        .insert({
          lot_id: lotId,
          auth_user_id: user.id,
          type: 'reconcile_lightning', // Usamos el nuevo tipo específico
          quantity: initialQuantity,
          unit_sign: lot.unit_sign,
          previous_remaining_quantity: lot.remaining_quantity,
          new_remaining_quantity: remainingQuantity,
          note: `Reconciliación de lote relámpago: cantidad inicial establecida a ${initialQuantity} ${lot.unit_sign || ''}`,
          created_at: now,
          updated_at: now
        });
      
      if (movementError) throw movementError;
      
      // 5. Actualizar estado local
      setLots(currentLots => 
        currentLots.map(l => 
          l.id === lotId 
            ? {
                ...l,
                initial_quantity: initialQuantity,
                remaining_quantity: remainingQuantity,
                quantity: remainingQuantity,
                status: 'active',
                needs_reconciliation: false,
                movements: [
                  {
                    type: 'reconcile_lightning',
                    quantity: initialQuantity,
                    unit: l.unit_sign,
                    timestamp: now,
                    note: `Reconciliación de lote relámpago: cantidad inicial establecida a ${initialQuantity} ${l.unit_sign || ''}`
                  },
                  ...(l.movements || [])
                ]
              }
            : l
        )
      );
      
      // Refrescar los lotes para asegurar datos actualizados
      setTimeout(() => fetchLots(), 300);
      
      // 6. Actualizar contador de lotes relámpago
      setLightningLotsCount(prev => Math.max(0, prev - 1));
      
      return {
        success: true,
        updatedLot: {
          ...lot,
          initial_quantity: initialQuantity,
          remaining_quantity: remainingQuantity,
          status: 'active',
          needs_reconciliation: false
        }
      };
    } catch (error) {
      console.error('Error reconciling lightning lot:', error);
      throw error;
    }
  };

  // Función para cargar la configuración del catálogo
  const fetchCatalogSettings = async () => {
    if (!business?.id) return;
    
    try {
      console.log('Cargando configuración del catálogo para el negocio:', business.id);
      
      // Primero intentar obtener de la columna catalog_settings en businesses
      let { data, error } = await supabase
        .from('businesses')
        .select('catalog_settings')
        .eq('id', business.id)
        .single();
        
      if (error || !data?.catalog_settings) {
        console.log('No se encontró catalog_settings en businesses, buscando en tabla catalog_settings');
        
        // Si no existe, intentar obtener de la tabla catalog_settings
        const { data: settingsData, error: settingsError } = await supabase
          .from('catalog_settings')
          .select('settings')
          .eq('business_id', business.id)
          .single();
          
        if (!settingsError && settingsData?.settings) {
          console.log('Configuración encontrada en tabla catalog_settings:', settingsData.settings);
          setCatalogStatus(settingsData.settings);
          return;
        }
      } else {
        console.log('Configuración encontrada en businesses.catalog_settings:', data.catalog_settings);
        setCatalogStatus(data.catalog_settings);
        return;
      }
      
      // Si no se encontró configuración, mantener los valores predeterminados
      console.log('No se encontró configuración del catálogo, usando valores predeterminados');
      
    } catch (error) {
      console.error('Error al cargar configuración del catálogo:', error);
      // Mantener los valores predeterminados en caso de error
    }
  };

  const value = {
    lots,
    loading,
    error,
    catalogStatus,
    addLot,
    updateLot,
    updateInventory,
    refreshLots: fetchLots,
    lightningLotsCount,
    pendingReviewsCount,
    startLotReview,
    finishLotReview,
    approveLotReview,
    updateCatalogStatus,
    reconcileLightningLot
  };

  return (
    <LotsContext.Provider value={value}>
      {children}
    </LotsContext.Provider>
  );
};

export const useLots = () => {
  const context = useContext(LotsContext);
  if (!context) {
    throw new Error('useLots must be used within a LotsProvider');
  }
  return context;
};